import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const AdminAuthContext = createContext(null);

export const AdminAuthProvider = ({ children }) => {
  const [adminToken, setAdminToken] = useState(null);
  const [adminUser, setAdminUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Initialize auth state from localStorage
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    const user = localStorage.getItem('adminUser') || localStorage.getItem('user');
    
    if (token && user) {
      setAdminToken(token);
      try {
        const parsedUser = JSON.parse(user);
        setAdminUser(parsedUser);
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
    setLoading(false);
  }, []);

  // Login function
  const login = useCallback((token, user) => {
    localStorage.setItem('adminToken', token);
    localStorage.setItem('adminUser', JSON.stringify(user));
    // DO NOT store in regular user keys - admin should be separate
    // localStorage.setItem('user', JSON.stringify(user));
    // localStorage.setItem('userId', user.id);
    
    setAdminToken(token);
    setAdminUser(user);
  }, []);

  // Logout function
  const logout = useCallback(() => {
    // Clear admin-specific data
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    // DO NOT clear regular user data - they are separate
    // localStorage.removeItem('user');
    // localStorage.removeItem('userId');
    setAdminToken(null);
    setAdminUser(null);
    navigate('/admin/login');
  }, [navigate]);

  // Check if user is authenticated
  const isAuthenticated = useCallback(() => {
    return !!adminToken || !!localStorage.getItem('adminToken');
  }, [adminToken]);

  // Get token for API calls
  const getToken = useCallback(() => {
    // First try to get from state
    if (adminToken) {
      return adminToken;
    }
    // Fallback to localStorage if state not yet initialized
    return localStorage.getItem('adminToken');
  }, [adminToken]);

  const value = {
    adminToken,
    adminUser,
    loading,
    login,
    logout,
    isAuthenticated,
    getToken
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
};
